import './salesDemo.css';

import { Typography, createMuiTheme } from '@material-ui/core';

import FactsPageBannerImage from './images/facts_page_banner.png';
import HomePageBannerImage from './images/home_page_banner.png';
import HomePageSectionImage from './images/faqs_page_banner.png';
import PercentageCircle from '../components/PercentageCircle'; // path will be relative to src/partner folder
import React from 'react';
import { getYear } from '../../src/util/getYear';
const PrivacyPolicyPDF = 'https://www.smurfitwestrock.com/privacy-notice';

const headingFontFamily = `"Slate Pro Bold", sans-serif`;
const bodyFontFamily = `"Slate Pro", sans-serif`;
const buttonBorderRadius = 0;
const primaryColor = '#00205B';
const secondaryColor = '#08B2FF';

const defaultTheme = createMuiTheme();

const theme = createMuiTheme({
  palette: {
    primary: {
      main: primaryColor,
    },
    secondary: {
      main: secondaryColor,
    },
  },
  typography: {
    fontFamily: bodyFontFamily,
    fontWeightMedium: 600,
    useNextVariants: true,
    htmlFontSize: 16,
    h1: {
      fontFamily: headingFontFamily,
      fontWeight: '400',
      fontSize: '48px',
      lineHeight: 1.2,
      letterSpacing: '-.9px',
      textTransform: 'uppercase',
    },
    h2: {
      fontFamily: headingFontFamily,
      fontWeight: '400',
      fontSize: '40px',
      lineHeight: 1.4,
      letterSpacing: '0px',
    },
    h3: {
      fontFamily: headingFontFamily,
      fontWeight: '400',
      fontSize: '32px',
      lineHeight: 1.4,
      letterSpacing: '0px',
    },
    h4: {
      fontFamily: headingFontFamily,
      fontWeight: '400',
      fontSize: '24px',
      lineHeight: 1.4,
      letterSpacing: '0.25px',
    },
    h5: {
      fontFamily: headingFontFamily,
      fontWeight: '700',
      fontSize: '20px',
      lineHeight: 1.4,
      letterSpacing: '0.25px',
    },
    h6: {
      fontFamily: headingFontFamily,
      fontWeight: '600',
      fontSize: '18px',
      lineHeight: 1.4,
      letterSpacing: '0.25px',
    },
    subtitle1: {
      fontWeight: '600',
      fontSize: '17px',
      lineHeight: 1.4,
      letterSpacing: '0.15px',
    },
    subtitle2: {
      fontWeight: '600',
      fontSize: '15px',
      lineHeight: 1.4,
      letterSpacing: '0.1px',
    },
    body1: {
      fontWeight: 'normal',
      fontSize: '17px',
      lineHeight: 1.4,
      letterSpacing: '0.5px',
    },
    body2: {
      fontWeight: 'normal',
      fontSize: '15px',
      lineHeight: 1.4,
      letterSpacing: '0.25px',
    },
    caption: {
      fontWeight: 'normal',
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '0.4px',
    },
    overline: {
      fontWeight: '700',
      fontSize: '14px',
      lineHeight: 1.4,
      letterSpacing: '1.25px',
    },
    button: {
      ...defaultTheme.typography.button,
      fontFamily: headingFontFamily,
      fontSize: '16px',
    },
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: buttonBorderRadius,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: buttonBorderRadius,
      },
    },
    MuiAccordion: {
      rounded: {
        '&:first-child': {
          borderTopLeftRadius: buttonBorderRadius,
          borderTopRightRadius: buttonBorderRadius,
        },
        '&:last-child': {
          borderBottomLeftRadius: buttonBorderRadius,
          borderBottomRightRadius: buttonBorderRadius,
        },
      },
    },
  },
});

export default {
  id: 'salesDemo',
  name: 'salesDemo',
  logo: null,
  styles: {
    muiTheme: {
      ...theme,
    },
    buttonBorderRadius: buttonBorderRadius,
  },
  appName: 'Recycle my pizza box',
  // meta tag values are used to generate the link preview when sharing link on social media
  metaTags: {
    title: `Pizza Box Recycling`,
    description: `A recent study commissioned by Smurfit Westrock, our primary box supplier and one of the largest box manufacturers in the U.S., estimated that 73% of the population has access to recycling programs for empty pizza boxes. Domino's is partnering with many of the key players in the paper recycling industry to help increase the recycling of pizza boxes.`,
    url: 'https://smurfitwestrock.com/', // TODO: update url once site is deployed
    thumbnailImage: '', // TODO: update url once site if deployed
  },
  footer: {
    text: `© ${getYear()} Smurfit Westrock. SMURFIT WESTROCK, SMURFIT WESTROCK Design are trademarks owned by Smurfit Westrock. All rights reserved.`,
    privacyPolicyUrl: PrivacyPolicyPDF,
  },
  homePage: {
    bannerImage: HomePageBannerImage,
    headline: 'ARE PIZZA BOXES RECYCLABLE?',
    body: (
      <>
        <Typography variant='body1' color='textPrimary' gutterBottom>
          It’s a common misconception, but many people don’t know that pizza
          boxes are recyclable. Smurfit Westrock is partnering with our customers to
          help encourage consumers to recycle pizza boxes and prove just how
          easy it is.
        </Typography>
        <Typography variant='body1' color='textPrimary'>
          Recycling corrugated pizza boxes could make a big impact on recycling
          at the local, state and national levels. We’re making progress, but
          there’s still work to do.
        </Typography>
      </>
    ),
    sectionImage: HomePageSectionImage,
    recyclingGuidelinesInstructions: 'Use our lookup tool at the top of the page to find out what the pizza box recycling guidelines in your area.'
  },
  factsPage: {
    banner: {
      bannerImage: FactsPageBannerImage,
      headline: 'Here are the facts',
      description: `We got to work together to make more progress:`,
    },
    optionalFact: {
      headline: '100%',
      description: 'Add your fact here!',
      source: 'Provide source',
      sourceLink: 'https://www.smurfitwestrock.com',
      icon: 'pizzabox',
    },
    headline: `More detailed facts and figures`,
    optionalLongFact: {
      headline: `Add Your Sustainable Practices`,
      sidebarContent: (
        <>
          <PercentageCircle percentage='100' title='50K' />
          <Typography
            color='textPrimary'
            variant='h6'
            gutterBottom
            style={{ textTransform: 'uppercase', fontWeight: 'bold' }}
          >
            50,000
          </Typography>
          <Typography variant='body2'>
            Nunc vel leo maximus, aliquam ante ac, condimentum elit. Morbi
            pellentesque nisi nec ultrices laoreet. Nulla blandit sem lorem.
            Aliquam egestas metus sed quam tempor, ut fermentum nunc hendrerit.
          </Typography>
        </>
      ),
      content: (
        <>
          <Typography variant='body1' gutterBottom>
            Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nam
            commodo, ante et consectetur dictum, orci lorem elementum tellus,
            eget placerat tellus urna at quam. In consectetur sagittis tortor
            sit amet ultricies. Proin felis arcu, cursus in aliquam quis, mollis
            at magna. Nam sed nisl in nisl pharetra elementum. Praesent quis
            orci at odio molestie feugiat fermentum vitae magna. Ut vel arcu
            fringilla, commodo justo eget, dignissim tellus. Suspendisse vitae
            porta urna. Fusce molestie sed sem non efficitur. Suspendisse
            eleifend ornare nisl, vel venenatis turpis commodo sit amet. Cras
            ultricies elit nec sagittis finibus. Nullam a accumsan lorem.
          </Typography>
          <Typography variant='body1' gutterBottom>
            Curabitur viverra velit eu libero posuere malesuada. Quisque quis
            turpis turpis. Etiam aliquam lacinia purus et faucibus. Vestibulum
            dapibus placerat turpis, eget lacinia ligula mollis a. Duis sem mi,
            semper nec urna sed, convallis scelerisque lorem. Vivamus at luctus
            tortor. Curabitur id est et est egestas maximus iaculis eu purus.
            Nam laoreet ligula nibh, nec viverra sem imperdiet ut. Aenean eget
            quam risus. Cras eu feugiat magna. Nullam rhoncus nunc eget finibus
            maximus. Maecenas vel mauris a libero consequat volutpat et vel
            magna. Duis sodales laoreet enim, ut consectetur leo vestibulum ut.
            Phasellus maximus, tellus non fringilla cursus, dui lectus
            sollicitudin est, eget pellentesque mauris libero a ligula.
          </Typography>
          <Typography variant='body1'>
            Mauris vel ex tempor dolor auctor ullamcorper sit amet sed odio.
            Aenean nisi quam, ultricies ac faucibus vel, dignissim ac justo.
            Mauris sit amet magna purus. Integer iaculis magna sapien, id
            maximus lectus rhoncus eget. Morbi auctor elit et sodales
            ullamcorper. Praesent efficitur sem purus. Nulla id justo eu lorem
            lacinia tincidunt. Quisque euismod lectus eget ligula dictum
            dapibus. Nulla facilisi.
          </Typography>
        </>
      ),
    },
  },
  faqsPage: {
    banner: {
      bannerImage: null,
    },
  },
  emailFeedback: {
    isSupported: false,
    privacy: null,
  },
};
